/**
 * API Configuration
 *
 * Centralized configuration for API client setup
 */

import { env } from './env';
import type { ApiClientConfig } from '@/types/api.types';

/**
 * Default timeout for API requests (30 seconds)
 */
export const DEFAULT_TIMEOUT = 30000;

/**
 * Default retry configuration
 *
 * Only retry on genuinely transient network/gateway errors:
 *   408 - Request Timeout
 *   429 - Too Many Requests (rate-limit, server asks us to back off)
 *   502 - Bad Gateway
 *   503 - Service Unavailable
 *   504 - Gateway Timeout
 *
 * 500 (Internal Server Error) is intentionally excluded — it indicates a
 * server-side application fault that will not resolve on retry, and retrying
 * mutating POST requests (e.g. checkout) risks creating duplicate transactions.
 */
export const DEFAULT_RETRY_CONFIG = {
  maxRetries: 3,
  retryDelay: 1000,
  retryableStatuses: [408, 429, 502, 503, 504],
} as const;

/**
 * API client configuration
 */
export const apiConfig: ApiClientConfig = {
  baseURL: env.apiBaseUrl,
  timeout: DEFAULT_TIMEOUT,
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  },
  withCredentials: false,
};

/**
 * API endpoint paths
 * These are appended to the baseURL
 */
export const API_PATHS = {
  health: '/health',
  company: '/api/v1/company/:companyId',
  providerConfig: '/provider-configuration/:companyId/Stripe',
  donationTypes: '/api/v1/donationtypes/:companyId',
  transaction: '/api/v1/transaction',
  createCheckoutSession: '/transaction/online/checkout',
  verifyPayment: '/transaction/online/verify-payment/:companyId/:sessionId',
} as const;

/**
 * Build full API endpoint URL
 * @param path - API path with optional parameters
 * @param params - Path parameters to replace
 * @param query - Query parameters
 * @returns Full endpoint URL
 *
 * @example
 * buildApiUrl('/api/v1/donationtypes/:companyId', { companyId: '123' }, { statusCode: 1 })
 * // Returns: "https://api.example.com/api/v1/donationtypes/123?statusCode=1"
 */
export function buildApiUrl(
  path: string,
  params?: Record<string, string | number>,
  query?: Record<string, string | number | boolean>
): string {
  let url = path;

  // Replace path parameters
  if (params) {
    Object.entries(params).forEach(([key, value]) => {
      url = url.replace(`:${key}`, String(value));
    });
  }

  // Add query parameters
  if (query && Object.keys(query).length > 0) {
    const queryString = Object.entries(query)
      .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`)
      .join('&');
    url += `?${queryString}`;
  }

  return url;
}

/**
 * Check if a status code is retryable
 */
export function isRetryableStatus(status: number): boolean {
  return DEFAULT_RETRY_CONFIG.retryableStatuses.includes(status as any);
}

/**
 * Calculate retry delay with exponential backoff
 */
export function calculateRetryDelay(attemptNumber: number): number {
  return DEFAULT_RETRY_CONFIG.retryDelay * Math.pow(2, attemptNumber - 1);
}
